import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import type { Pack } from "@/lib/types/deck-builder";
import type { Unit } from "@/lib/types/unit";
import { Deck } from "@/lib/classes/deck";
import { getUnitImagePath } from "@/lib/utils/unit-images";
import { UnitInfoDialog } from "./UnitInfoDialog";
import { useComparisonModals } from "./ComparisonModalManager";
import { Info } from "lucide-react";

interface UnitSelectionDialogProps {
  open: boolean;
  onClose: () => void;
  pack: Pack | null;
  unit: Unit | null;
  deck: Deck;
  onConfirm: (pack: Pack, veterancy: number, transport?: Unit) => void;
  preselectedVeterancy?: number;
  hideVeterancySelection?: boolean;
}

export function UnitSelectionDialog({
  open,
  onClose,
  pack,
  unit,
  deck,
  onConfirm,
  preselectedVeterancy,
  hideVeterancySelection = false,
}: UnitSelectionDialogProps) {
  const [selectedVeterancy, setSelectedVeterancy] = useState<number>(0);
  const [selectedTransport, setSelectedTransport] = useState<Unit | null | undefined>(undefined);
  const [imageError, setImageError] = useState(false);
  const [transportImageErrors, setTransportImageErrors] = useState<Set<string>>(new Set());
  const [infoDialogOpen, setInfoDialogOpen] = useState(false);
  const [selectedTransportForInfo, setSelectedTransportForInfo] = useState<Unit | null>(null);
  const { openComparisonModal } = useComparisonModals();

  // Reset state when dialog opens/closes or pack changes
  useEffect(() => {
    if (open && pack) {
      if (preselectedVeterancy !== undefined) {
        setSelectedVeterancy(preselectedVeterancy);
      } else {
        const defaultVeterancy = Deck.getDefaultVeterancyForPack(pack, unit ?? undefined);
        setSelectedVeterancy(defaultVeterancy >= 0 ? defaultVeterancy : 0);
      }
      setImageError(false);
      setTransportImageErrors(new Set());
      
      const transports = deck.getTransportsForPack(pack);
      const canUseWithoutTransport = deck.getPackAvailableWithoutTransport(pack);
      
      // Set initial transport selection
      if (transports && transports.length > 0 && !canUseWithoutTransport) {
        // Transport required - select first one by default
        setSelectedTransport(transports[0]);
      } else if (canUseWithoutTransport) {
        // Can use without transport - default to no transport
        setSelectedTransport(null);
      } else {
        setSelectedTransport(undefined);
      }
    }
  }, [open, pack, deck, unit]);

  // Prevent escape key from closing parent dialog when info dialog is open
  useEffect(() => {
    if (infoDialogOpen) {
      const handleEscape = (e: KeyboardEvent) => {
        if (e.key === "Escape") {
          e.stopPropagation();
        }
      };
      document.addEventListener("keydown", handleEscape, true);
      return () => {
        document.removeEventListener("keydown", handleEscape, true);
      };
    }
  }, [infoDialogOpen]);

  if (!pack || !unit) {
    return null;
  }

  const veterancyQuantities = Deck.getVeterancyQuantitiesForPack(pack, unit);
  const transports = deck.getTransportsForPack(pack);
  const canUseWithoutTransport = deck.getPackAvailableWithoutTransport(pack);
  const requiresTransport = transports && transports.length > 0 && !canUseWithoutTransport;

  const handleConfirm = () => {
    if (requiresTransport && selectedTransport === undefined) {
      // Transport is required but not selected
      return;
    }
    
    onConfirm(pack, selectedVeterancy, selectedTransport || undefined);
    onClose();
  };

  const unitQuantity = veterancyQuantities[selectedVeterancy] || 0;
  const transportCost = selectedTransport?.commandPoints || 0;
  const unitCost = unit.commandPoints || 0;
  const totalCost = (unitCost + transportCost) * unitQuantity;

  const handleTransportImageError = (descriptorName: string) => {
    setTransportImageErrors((prev) => new Set(prev).add(descriptorName));
  };

  const handleInfoClick = (e: React.MouseEvent, transport: Unit) => {
    e.stopPropagation(); // Prevent triggering transport selection
    setSelectedTransportForInfo(transport);
    setInfoDialogOpen(true);
  };

  const handleInfoDialogOpenChange = (open: boolean) => {
    setInfoDialogOpen(open);
    // Don't close the parent dialog when info dialog closes
  };

  const handleParentDialogOpenChange = (newOpen: boolean) => {
    // Don't close if the info dialog is open
    if (!newOpen && infoDialogOpen) {
      return;
    }
    onClose();
  };

  return (
    <>
    <Dialog open={open} onOpenChange={handleParentDialogOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-4 mb-2">
            {/* Unit Image */}
            {!imageError ? (
              <img
                src={getUnitImagePath(unit.descriptorName)}
                alt={unit.name}
                className="w-20 h-20 object-contain bg-muted rounded border flex-shrink-0"
                onError={() => setImageError(true)}
              />
            ) : (
              <div className="w-20 h-20 bg-muted rounded border flex items-center justify-center flex-shrink-0">
                <span className="text-xs text-muted-foreground">No Image</span>
              </div>
            )}
            <div className="flex-1 min-w-0">
              <DialogTitle className="truncate">Add {unit.name}</DialogTitle>
              <DialogDescription>
                Select veterancy level and transport option
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-6">
          {/* Veterancy Selection - only show if not hidden */}
          {!hideVeterancySelection && (
            <div className="space-y-3">
              <Label>Veterancy Level</Label>
              <RadioGroup
                value={selectedVeterancy.toString()}
                onValueChange={(value) => setSelectedVeterancy(parseInt(value, 10))}
              >
                <div className="space-y-2">
                  {veterancyQuantities.map((quantity, index) => (
                    <Card
                      key={index}
                      className={`cursor-pointer transition-colors ${
                        selectedVeterancy === index
                          ? "border-primary bg-primary/5"
                          : "hover:bg-accent"
                      }`}
                      onClick={() => setSelectedVeterancy(index)}
                    >
                      <CardContent className="p-3">
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value={index.toString()} id={`veterancy-${index}`} />
                          <Label
                            htmlFor={`veterancy-${index}`}
                            className="flex-1 cursor-pointer font-normal"
                          >
                            <div className="flex items-center justify-between">
                              <span>
                                {index === 0 ? "Regular" : index === 1 ? "Veteran" : index === 2 ? "Elite" : `Level ${index}`}
                              </span>
                              <span className="text-sm text-muted-foreground">
                                {quantity} units
                              </span>
                            </div>
                          </Label>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </RadioGroup>
            </div>
          )}

          {/* Transport Selection */}
          {transports && transports.length > 0 && (
            <div className="space-y-3">
              <Label>
                Transport {requiresTransport && <span className="text-destructive">*</span>}
              </Label>
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {canUseWithoutTransport && (
                  <Card
                    className={`cursor-pointer transition-colors ${
                      selectedTransport === null
                        ? "border-primary bg-primary/5"
                        : "hover:bg-accent"
                    }`}
                    onClick={() => setSelectedTransport(null)}
                  >
                    <CardContent className="p-3">
                      <div className="flex items-center justify-between">
                        <span className="font-medium">No Transport</span>
                        <span className="text-sm text-muted-foreground">0 CP</span>
                      </div>
                    </CardContent>
                  </Card>
                )}
                {transports.map((transport) => {
                  const hasImageError = transportImageErrors.has(transport.descriptorName);
                  return (
                    <Card
                      key={transport.descriptorName}
                      className={`cursor-pointer transition-colors overflow-hidden relative ${
                        selectedTransport?.descriptorName === transport.descriptorName
                          ? "border-primary bg-primary/5"
                          : "hover:bg-accent"
                      }`}
                      onClick={() => setSelectedTransport(transport)}
                    >
                      <CardContent className="p-3 relative z-10">
                        <div className="flex items-center justify-between">
                          <div className="flex-1 min-w-0 pr-4">
                            <span className="font-medium">{transport.name}</span>
                            <span className="text-sm text-muted-foreground block">
                              {transport.commandPoints} CP
                            </span>
                          </div>
                        </div>
                      </CardContent>
                      {/* Overlay Info Button (top-right) */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          // On desktop, open comparison modal directly; on mobile, use regular modal
                          if (typeof window !== "undefined" && window.innerWidth >= 1024) {
                            openComparisonModal(transport);
                          } else {
                            handleInfoClick(e, transport);
                          }
                        }}
                        className="absolute top-[2px] right-[2px] w-7 h-7 bg-black/60 hover:bg-black/80 rounded-full flex items-center justify-center shadow-lg transition-colors z-20"
                        aria-label="Transport information"
                      >
                        <Info className="h-3.5 w-3.5 text-white" />
                      </button>
                      {!hasImageError && (
                        <>
                          <img
                            src={getUnitImagePath(transport.descriptorName)}
                            alt=""
                            className="absolute inset-y-0 right-0 w-2/3 h-full object-cover opacity-100 pointer-events-none"
                            onError={() => handleTransportImageError(transport.descriptorName)}
                          />
                          <div
                            className="absolute inset-0 pointer-events-none"
                            style={{
                              background: 'linear-gradient(to right, var(--card) 0%, var(--card) 40%, transparent 100%)',
                            }}
                          />
                        </>
                      )}
                    </Card>
                  );
                })}
              </div>
            </div>
          )}

          {/* Cost Summary */}
          <div className="border-t pt-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Unit Cost:</span>
              <span>{unitCost} CP × {unitQuantity} = {unitCost * unitQuantity} CP</span>
            </div>
            {transportCost > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Transport Cost:</span>
                <span>{transportCost} CP × {unitQuantity} = {transportCost * unitQuantity} CP</span>
              </div>
            )}
            <div className="flex justify-between font-semibold pt-2 border-t">
              <span>Total Cost:</span>
              <span>{totalCost} CP</span>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={requiresTransport && selectedTransport === undefined}
          >
            Add to Deck
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    {selectedTransportForInfo && (
      <UnitInfoDialog
        open={infoDialogOpen}
        onOpenChange={handleInfoDialogOpenChange}
        unit={selectedTransportForInfo}
      />
    )}
  </>
  );
}

